import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAppStore } from '../store/useAppStore';

// Socket.io connects to the server root, not the /api path
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const SOCKET_URL = API_URL.replace(/\/api$/, ''); // Remove /api suffix for socket connection

interface BalanceAdjustmentEvent {
  type: 'credit' | 'debit';
  amount: number;
  newBalance: number;
  reason: string | null;
  notification: {
    id: string;
    type: 'success' | 'warning' | 'info' | 'refund';
    title: string;
    message: string;
    createdAt: Date;
    read: boolean;
  };
}

interface WithdrawalCompletedEvent {
  withdrawalId: string;
  amount: number;
  paymentMethod: string;
  transactionRef: string | null;
}

interface UseSocketOptions {
  onBalanceAdjustment?: (data: BalanceAdjustmentEvent) => void;
  onWithdrawalCompleted?: (data: WithdrawalCompletedEvent) => void;
}

export function useSocket(options: UseSocketOptions = {}) {
  const socketRef = useRef<Socket | null>(null);
  const optionsRef = useRef(options);
  const user = useAppStore((state) => state.user);
  const updateBalance = useAppStore((state) => state.updateBalance);

  // Keep options ref up to date without triggering reconnects
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const connect = useCallback(() => {
    if (!user?.telegramId) return;
    
    // Don't reconnect if already connected
    if (socketRef.current?.connected) return;
    
    // Clean up existing disconnected socket
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current = null;
    }
    
    try {
      console.log('[Socket] Connecting to:', SOCKET_URL, 'for user:', user.telegramId);
      const socket = io(SOCKET_URL, {
        transports: ['websocket', 'polling'],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 10000,
      });
      
      socket.on('connect', () => {
        console.log('[Socket] Connected:', socket.id);
        // Subscribe to user's room
        socket.emit('subscribe', user.telegramId);
      });

      socket.on('disconnect', (reason) => {
        console.log('[Socket] Disconnected:', reason);
      });

      socket.on('connect_error', (error) => {
        console.error('[Socket] Connection error:', error.message);
      });

      // Listen for balance adjustments
      socket.on('balance-adjustment', (data: BalanceAdjustmentEvent) => {
        console.log('[Socket] Balance adjustment received:', data);
        
        // Update the store balance
        updateBalance(data.newBalance);
        
        // Call the callback if provided
        optionsRef.current.onBalanceAdjustment?.(data);
      });

      // Listen for withdrawal completions
      socket.on('withdrawal-completed', (data: WithdrawalCompletedEvent) => {
        console.log('[Socket] Withdrawal completed received:', data);
        
        // Call the callback if provided
        optionsRef.current.onWithdrawalCompleted?.(data);
      });

      socketRef.current = socket;
    } catch (error) {
      console.error('[Socket] Failed to initialize:', error);
    }
  }, [user?.telegramId, updateBalance]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  useEffect(() => {
    connect();
    
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    socket: socketRef.current,
    isConnected: socketRef.current?.connected ?? false,
    connect,
    disconnect,
  };
}
